/**
 * Map Layout Component
 *
 * Complex layout for map view routes.
 * Handles map-specific logic, panels, modals, and state management.
 *
 * This is the original Layout function from App.tsx, extracted for better organization.
 */

import { useLocation, useNavigate } from 'react-router-dom'
import React, {
  useState,
  ReactNode,
  useMemo,
  useEffect,
  lazy,
  Suspense,
} from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchZones, addZone } from '../store/slices/zoneSlice'
import type { Zone } from '../features/zones/types'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import Notification from './Notification'
import { formatCreateError } from '../utils/errorMessages'

// Lazy load map-specific components
const MapInfoModal = lazy(
  () => import('../features/zones/components/MapInfoModal')
)
const CreateZonePanel = lazy(
  () => import('../features/zones/components/CreateZonePanel')
)
const ViewZonesPanel = lazy(
  () => import('../features/zones/components/ViewZonesPanel')
)
const EditZonePanel = lazy(
  () => import('../features/zones/components/EditZonePanel')
)
const ZoneDetailsPanel = lazy(
  () => import('../features/zones/components/ZoneDetailsPanel')
)
const ZoneCreateSuccessModal = lazy(
  () => import('../features/zones/components/ZoneCreateSuccessModal')
)
const ZoneUpdateSuccessModal = lazy(
  () => import('../features/zones/components/ZoneUpdateSuccessModal')
)

interface MapLayoutProps {
  readonly children: ReactNode
}

type PanelType = 'create' | 'view' | 'edit' | 'details' | null

interface PanelConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>
  zIndex: number
  getProps: (
    data: Zone | undefined,
    context: PanelContext
  ) => Record<string, unknown>
  shouldRender?: (data: Zone | undefined, context: PanelContext) => boolean
}

type PanelContext = {
  activePanel: PanelType
  zoneFormRef: React.RefObject<{ reset: () => void } | null>
  formResetTrigger: number
  handleCreateClose: () => void
  handleCreateSubmit: (zoneData: {
    name: string
    score: number
    notes?: string
  }) => Promise<void>
  closeAllPanels: () => void
  openPanel: (panelType: PanelType, data?: Zone | undefined) => void
  handleEditSave: (zoneId?: string) => void
  setDrawingMode: (mode: 'draw' | 'edit' | 'grid' | 'view') => void
  drawingMode: 'draw' | 'edit' | 'grid' | 'view'
  gridSize: number
  setGridSize: (size: number) => void
  gridCellPosition: [number, number] | null
  setGridCellPosition: (position: [number, number] | null) => void
  calculatedDistance: number
  calculatedArea: number
  isCalculatingArea: boolean
  isCalculatingDistance: boolean
  isMapView: boolean
  selectedZone: Zone | null
  editingZone: Zone | null
  editingZoneId: string | null
  hasPolygon: boolean
  setHasPolygon: (hasPolygon: boolean) => void
  blockMapInteractionsRef: React.MutableRefObject<{
    blocked: boolean
    x: number
    y: number
    time: number
  }>
}

export default function MapLayout({ children }: MapLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { zones, loading } = useAppSelector(state => state.zones)
  const hasFetchedZonesRef = React.useRef(false)
  const [isCalculatingArea, setIsCalculatingArea] = useState(false)
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false)

  // Fetch zones once on initial load
  useEffect(() => {
    if (!hasFetchedZonesRef.current) {
      dispatch(fetchZones())
      hasFetchedZonesRef.current = true
    }
  }, [dispatch])

  // Helper function to get zone by ID
  const getZoneById = React.useCallback(
    (id: string): Zone | undefined => {
      return zones.find(zone => zone.id === id)
    },
    [zones]
  )

  const [activePanel, setActivePanel] = useState<PanelType>(null)
  const [panelData, setPanelData] = useState<Record<string, Zone | undefined>>(
    {}
  )
  const [drawingMode, setDrawingMode] = useState<
    'draw' | 'edit' | 'grid' | 'view'
  >('draw')
  const [mapType, setMapType] = useState<'street' | 'satellite'>('street')
  const mobileMenuToggleRef = React.useRef<(() => void) | null>(null)

  const handleMobileMenuToggle = React.useCallback(() => {
    if (mobileMenuToggleRef.current) {
      mobileMenuToggleRef.current()
    } else {
      // eslint-disable-next-line no-console
      console.warn('Mobile menu toggle function not available')
    }
  }, [])

  const [gridSize, setGridSize] = useState<number>(0.1)
  const [gridCellPosition, setGridCellPosition] = useState<
    [number, number] | null
  >(null)
  const [calculatedDistance, setCalculatedDistance] = useState(0)
  const [calculatedArea, setCalculatedArea] = useState(0)
  const [mapSearchHandler, setMapSearchHandler] = useState<{
    search: (lat: number, lng: number, zoom: number) => void
  } | null>(null)

  const [isMapInfoOpen, setIsMapInfoOpen] = useState(false)
  const [hasPolygon, setHasPolygon] = useState(false)

  // Derived state
  const isCreateOpen = activePanel === 'create'
  const selectedZone = activePanel === 'details' ? panelData['details'] : null
  const editingZone = activePanel === 'edit' ? panelData['edit'] : null

  const editingZoneId = React.useMemo(() => {
    return editingZone?.id ?? null
  }, [editingZone?.id])

  // eslint-disable-next-line no-console
  console.log('[MapLayout] Render state', {
    activePanel,
    isCreateOpen,
    drawingMode,
    editingZoneId,
    hideDrawControls: !isCreateOpen && !editingZoneId,
  })

  const [formData, setFormData] = useState<{
    name: string
    score: number
    notes?: string
  } | null>(null)
  const [formResetTrigger, setFormResetTrigger] = useState(0)
  const zoneFormRef = React.useRef<{ reset: () => void }>(null)
  const [showCreateSuccessModal, setShowCreateSuccessModal] = useState(false)
  const [createdZone, setCreatedZone] = useState<Zone | null>(null)
  const [showUpdateSuccessModal, setShowUpdateSuccessModal] = useState(false)
  const [updatedZone, setUpdatedZone] = useState<Zone | null>(null)
  const [notification, setNotification] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
    isOpen: boolean
  }>({
    type: 'error',
    title: '',
    message: '',
    isOpen: false,
  })

  const showNotification = React.useCallback(
    (type: 'success' | 'error', title: string, message: string) => {
      setNotification({
        type,
        title,
        message,
        isOpen: true,
      })
    },
    []
  )

  const navigationProcessedRef = React.useRef<string | null>(null)
  const isClosingPanelRef = React.useRef<boolean>(false)

  const closeAllPanels = React.useCallback(() => {
    isClosingPanelRef.current = true
    setActivePanel(null)
    setPanelData({})
    setDrawingMode('draw')
    setGridCellPosition(null)
    setHasPolygon(false)
    navigate(location.pathname, { replace: true, state: null })
    globalThis.history.replaceState({}, document.title)
    navigationProcessedRef.current = null
    setTimeout(() => {
      isClosingPanelRef.current = false
    }, 200)
  }, [navigate, location.pathname])

  const openPanel = React.useCallback(
    (panelType: PanelType, data?: Zone | undefined) => {
      // eslint-disable-next-line no-console
      console.log('[MapLayout] openPanel called', {
        panelType,
        hasData: !!data,
        currentDrawingMode: drawingMode,
      })
      if (data !== undefined && panelType) {
        setPanelData(prev => ({ ...prev, [panelType]: data }))
      }

      if (panelType === 'create') {
        // eslint-disable-next-line no-console
        console.log('[MapLayout] Setting drawing mode to draw')
        setDrawingMode('draw')
      } else if (panelType === 'details') {
        setDrawingMode('view')
      } else if (panelType === 'edit') {
        setDrawingMode('edit')
      }

      setActivePanel(panelType)
      // eslint-disable-next-line no-console
      console.log('[MapLayout] Panel opened', {
        panelType,
        activePanel: panelType,
      })
    },
    [drawingMode]
  )

  const createZoneIfReady = React.useCallback(
    async (formDataToUse?: { name: string; score: number; notes?: string }) => {
      const currentFormData = formDataToUse ?? formData

      const polygonData =
        (await (
          globalThis as {
            _drawControlGetCreatePolygon?: () => Promise<{
              coordinates: number[][]
              area: number
            } | null>
          }
        )._drawControlGetCreatePolygon?.()) ?? null

      if (currentFormData?.name && polygonData) {
        try {
          const result = await dispatch(
            addZone({
              name: currentFormData.name,
              coordinates: polygonData.coordinates,
              score: currentFormData.score,
              area: polygonData.area,
              ...(currentFormData.notes && { notes: currentFormData.notes }),
            })
          ).unwrap()

          setFormData(null)
          setHasPolygon(false)
          setFormResetTrigger(prev => prev + 1)
          zoneFormRef.current?.reset()

          setActivePanel(null)
          setPanelData({})
          setCreatedZone(result)
          setShowCreateSuccessModal(true)
          return true
        } catch (error: unknown) {
          // eslint-disable-next-line no-console
          console.error('❌ Error creating zone:', error)
          const { title, message } = formatCreateError(error)
          showNotification('error', title, message)
          return false
        }
      }
      return false
    },
    [dispatch, formData, zoneFormRef, showNotification]
  )

  const handleCreateClose = React.useCallback(() => {
    closeAllPanels()
    setDrawingMode('draw')
    setCalculatedDistance(0)
    setGridCellPosition(null)
    setHasPolygon(false)
    setFormData(null)
    if (zoneFormRef.current) {
      zoneFormRef.current.reset()
    }
  }, [closeAllPanels])

  const handleCreateSubmit = React.useCallback(
    async (zoneData: unknown) => {
      const singleZoneData = zoneData as {
        name: string
        score: number
        notes?: string
      }
      if (!singleZoneData || typeof singleZoneData !== 'object') {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Invalid form data received:', singleZoneData)
        return
      }

      const name = singleZoneData.name?.trim() ?? ''
      if (!name) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Zone name is required')
        return
      }

      const parsedScore = Number(singleZoneData.score)
      const score = Number.isNaN(parsedScore)
        ? 0
        : Math.min(6, Math.max(0, Math.round(parsedScore)))

      const formDataToStore = {
        name,
        score,
        ...(singleZoneData.notes?.trim()
          ? { notes: singleZoneData.notes.trim() }
          : {}),
      }

      // Grid mode
      if (drawingMode === 'grid') {
        const gridCellData =
          (
            globalThis as {
              _drawControlGetGridCell?: () => {
                coordinates: number[][]
                area: number
              }
            }
          )._drawControlGetGridCell?.() ?? null
        if (!gridCellData) {
          showNotification(
            'error',
            'Grid Cell Required',
            'Please position a grid cell on the map before creating the zone.'
          )
          return
        }

        try {
          const result = await dispatch(
            addZone({
              ...formDataToStore,
              coordinates: gridCellData.coordinates,
              area: gridCellData.area,
            })
          ).unwrap()

          setFormData(null)
          setHasPolygon(false)
          setFormResetTrigger(prev => prev + 1)
          zoneFormRef.current?.reset()
          setGridCellPosition(null)
          setActivePanel(null)
          setPanelData({})
          setShowCreateSuccessModal(true)
          setCreatedZone(result)
        } catch (error: unknown) {
          // eslint-disable-next-line no-console
          console.error('❌ Error creating zone:', error)
          const { title, message } = formatCreateError(error)
          showNotification('error', title, message)
        }
        return
      }

      // Draw mode
      const polygonData =
        (await (
          globalThis as {
            _drawControlGetCreatePolygon?: () => Promise<{
              coordinates: number[][]
              area: number
            } | null>
          }
        )._drawControlGetCreatePolygon?.()) ?? null
      if (!polygonData) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Polygon not drawn yet. Please draw a polygon first.')
        showNotification(
          'error',
          'Zone Shape Required',
          'Please draw a polygon on the map before creating the zone.'
        )
        return
      }

      await createZoneIfReady(formDataToStore)
    },
    [createZoneIfReady, dispatch, drawingMode, zoneFormRef, showNotification]
  )

  const handleEditSave = React.useCallback(
    (zoneId?: string) => {
      if (zoneId && getZoneById) {
        const updated = getZoneById(zoneId)
        if (updated) {
          setUpdatedZone(updated)
          setShowUpdateSuccessModal(true)
        }
      }
    },
    [getZoneById]
  )

  const blockMapInteractionsRef = React.useRef<{
    blocked: boolean
    x: number
    y: number
    time: number
  }>({
    blocked: false,
    x: 0,
    y: 0,
    time: 0,
  })

  const panelContext = useMemo(
    () => ({
      activePanel,
      zoneFormRef,
      formResetTrigger,
      handleCreateClose,
      handleCreateSubmit,
      closeAllPanels,
      openPanel,
      handleEditSave,
      setDrawingMode,
      drawingMode,
      gridSize,
      setGridSize,
      gridCellPosition,
      setGridCellPosition,
      calculatedDistance,
      calculatedArea,
      isCalculatingArea,
      isCalculatingDistance,
      isMapView: true,
      selectedZone: selectedZone ?? null,
      editingZone: editingZone ?? null,
      editingZoneId,
      hasPolygon,
      setHasPolygon,
      blockMapInteractionsRef,
    }),
    [
      activePanel,
      formResetTrigger,
      handleCreateClose,
      handleCreateSubmit,
      closeAllPanels,
      openPanel,
      handleEditSave,
      setDrawingMode,
      drawingMode,
      gridSize,
      setGridSize,
      gridCellPosition,
      setGridCellPosition,
      calculatedDistance,
      calculatedArea,
      isCalculatingArea,
      isCalculatingDistance,
      selectedZone,
      editingZone,
      editingZoneId,
      hasPolygon,
      setHasPolygon,
      blockMapInteractionsRef,
    ]
  )

  // Panel configuration
  const PANEL_CONFIG: Record<string, PanelConfig> = {
    create: {
      component: CreateZonePanel,
      zIndex: 1000,
      getProps: (_data, context) => ({
        open: context.activePanel === 'create',
        formRef: context.zoneFormRef,
        resetTrigger: context.formResetTrigger,
        onClose: context.handleCreateClose,
        onCreate: context.handleCreateSubmit,
        onDrawingModeChange: context.setDrawingMode,
        drawingMode: context.drawingMode,
        gridSize: context.gridSize,
        onGridSizeChange: context.setGridSize,
        gridCellPosition: context.gridCellPosition,
        onGridCellPositionChange: context.setGridCellPosition,
        calculatedDistance: context.calculatedDistance,
        calculatedArea: context.calculatedArea,
        isCalculatingArea: context.isCalculatingArea,
        isCalculatingDistance: context.isCalculatingDistance,
        hasPolygon: context.hasPolygon ?? false,
      }),
    },
    view: {
      component: ViewZonesPanel,
      zIndex: 1000,
      getProps: (_data, context) => ({
        open: context.activePanel === 'view',
        onClose: context.closeAllPanels,
        onZoneClick: (zone: Zone) => context.openPanel('details', zone),
      }),
    },
    edit: {
      component: EditZonePanel,
      zIndex: 1000,
      getProps: (data, context) => ({
        open: context.activePanel === 'edit',
        zone: data,
        onClose: context.closeAllPanels,
        onSave: (zoneId?: string) => {
          context.closeAllPanels()
          setTimeout(() => {
            context.handleEditSave(zoneId)
          }, 100)
        },
        calculatedArea: context.calculatedArea,
        isCalculatingArea: context.isCalculatingArea,
        editingZoneId: context.editingZoneId,
        onEditingZoneIdChange: (_zoneId: string | null) => {
          // Handler for editing zone ID changes
        },
      }),
      shouldRender: (data, context) => context.isMapView && !!data,
    },
    details: {
      component: ZoneDetailsPanel,
      zIndex: 1000,
      getProps: (data, context) => ({
        zone: data,
        onClose: () => context.openPanel(null),
        onBackToList: () => context.openPanel('view'),
        onEdit: (zone: Zone) => context.openPanel('edit', zone),
      }),
      shouldRender: (data, context) => context.isMapView && !!data,
    },
  }

  React.useEffect(() => {
    if (isCreateOpen) {
      setCalculatedDistance(0)
      setHasPolygon(false)
    }
  }, [isCreateOpen])

  React.useEffect(() => {
    if (!isCreateOpen) {
      setHasPolygon(false)
      return
    }

    const checkPolygon = async () => {
      if (drawingMode === 'draw') {
        const polygonData = await (
          globalThis as {
            _drawControlGetCreatePolygon?: () => Promise<{
              coordinates: number[][]
              area: number
            } | null>
          }
        )._drawControlGetCreatePolygon?.()
        const newHasPolygon = !!polygonData
        setHasPolygon(newHasPolygon)
      } else if (drawingMode === 'grid') {
        const newHasPolygon = !!gridCellPosition
        setHasPolygon(newHasPolygon)
      } else {
        setHasPolygon(false)
      }
    }

    checkPolygon()
    const interval = setInterval(checkPolygon, 500)
    return () => clearInterval(interval)
  }, [isCreateOpen, drawingMode, gridCellPosition])

  const handleNavigation = (
    action: string,
    zoneId: string,
    openPanel: (panel: PanelType, data?: Zone) => void,
    getZoneById: (id: string) => Zone | undefined
  ) => {
    const zone = getZoneById(zoneId)
    if (zone) {
      openPanel(action === 'edit' ? 'edit' : 'details', zone)
    } else {
      // eslint-disable-next-line no-console
      console.warn('MapLayout: Zone not found in Redux store', { zoneId })
    }
  }

  const processNavigation = React.useCallback(
    (action: string | undefined, zoneId: string | undefined) => {
      if (!action || !zoneId) return

      const navKey = `${action}-${zoneId}`
      const isPanelAlreadyOpen =
        (activePanel === 'edit' && action === 'edit') ||
        (activePanel === 'details' && action === 'view')

      if (navigationProcessedRef.current === navKey) {
        // Only clear state if panel is actually open, otherwise let it process
        if (isPanelAlreadyOpen) {
          navigate(location.pathname, { replace: true, state: null })
          return
        }
        // If not open yet, continue processing (might be a retry)
      }

      // Don't process if zones aren't loaded yet
      if (loading || zones.length === 0 || !getZoneById) {
        return
      }

      navigationProcessedRef.current = navKey

      const timeoutId = setTimeout(() => {
        if (isClosingPanelRef.current) return

        const currentState = location.state as {
          action?: string
          zoneId?: string
        } | null
        if (currentState?.action !== action || currentState?.zoneId !== zoneId)
          return

        handleNavigation(action, zoneId, openPanel, getZoneById)

        globalThis.history.replaceState({}, document.title)
      }, 100)

      return timeoutId
    },
    [
      activePanel,
      loading,
      zones.length,
      getZoneById,
      location.state,
      location.pathname,
      navigate,
      openPanel,
    ]
  )

  // Process navigation when location state changes, but only if zones are loaded
  React.useEffect(() => {
    // Don't process if zones aren't loaded yet
    if (loading || zones.length === 0) {
      return undefined
    }

    const locationState = location.state as {
      action?: string
      zoneId?: string
    } | null
    const { action, zoneId } = locationState ?? {}

    // eslint-disable-next-line no-console
    console.log('[MapLayout] Navigation effect', {
      action,
      zoneId,
      hasState: !!locationState,
    })

    if (action === 'create') {
      // eslint-disable-next-line no-console
      console.log('[MapLayout] Opening create panel')
      openPanel('create')
      navigate(location.pathname, { replace: true, state: null })
      return undefined
    }

    if (action && zoneId) {
      const timeoutId = processNavigation(action, zoneId)

      return () => {
        if (timeoutId) clearTimeout(timeoutId)
      }
    }

    return undefined
  }, [
    location.state,
    processNavigation,
    loading,
    zones.length,
    openPanel,
    navigate,
    location.pathname,
  ])

  // Re-process navigation when zones finish loading (in case navigation happened before zones loaded)
  React.useEffect(() => {
    if (!loading && zones.length > 0) {
      const locationState = location.state as {
        action?: string
        zoneId?: string
      } | null
      const { action, zoneId } = locationState ?? {}

      if (action && zoneId) {
        // Reset navigation processed ref to allow re-processing
        const navKey = `${action}-${zoneId}`
        if (navigationProcessedRef.current !== navKey) {
          // Small delay to ensure everything is ready
          const timeoutId = setTimeout(() => {
            processNavigation(action, zoneId)
          }, 200)
          return () => clearTimeout(timeoutId)
        }
      }
    }

    return undefined
  }, [loading, zones.length, location.state, processNavigation])

  return (
    <div className='h-screen w-screen bg-gray-50'>
      <div className='h-full w-full grid grid-cols-1 lg:grid-cols-[280px_1fr]'>
        <Sidebar
          onCreate={() => {
            openPanel('create')
          }}
          mapType={mapType}
          onMapTypeChange={setMapType}
          isMapView={true}
          onMobileMenuToggle={toggleFn => {
            mobileMenuToggleRef.current = toggleFn
          }}
        />
        <div className='relative bg-white overflow-hidden'>
          <Topbar
            onViewZones={() => {
              openPanel('view')
            }}
            onSearch={(lat, lng, zoom) => {
              if (mapSearchHandler) {
                mapSearchHandler.search(lat, lng, zoom)
              }
            }}
            onMobileMenuToggle={handleMobileMenuToggle}
            isMapInfoOpen={isMapInfoOpen}
            onMapInfoClose={() => setIsMapInfoOpen(false)}
          />
          {React.cloneElement(
            children as React.ReactElement<Record<string, unknown>>,
            {
              drawingMode,
              gridSize,
              gridCellPosition,
              onGridCellPositionChange: setGridCellPosition,
              hideDrawControls: !isCreateOpen && !editingZoneId,
              selectedZone: selectedZone || null,
              setSelectedZone: (zone: Zone | null) => {
                if (zone) {
                  openPanel('details', zone)
                } else {
                  closeAllPanels()
                }
              },
              onDistanceCalculated: setCalculatedDistance,
              onAreaCalculated: setCalculatedArea,
              onAreaCalculatingChange: setIsCalculatingArea,
              onDistanceCalculatingChange: setIsCalculatingDistance,
              onMapReady: setMapSearchHandler,
              onBackToList: () => {
                openPanel('view')
              },
              onEdit: (zone: Zone) => {
                openPanel('edit', zone)
              },
              editingZoneId: editingZoneId,
              hasPolygon: hasPolygon,
              onPolygonChange: setHasPolygon,
              mapType: mapType,
              onMapTypeChange: setMapType,
              onError: (title: string, message: string) => {
                showNotification('error', title, message)
              },
              blockMapInteractionsRef,
            }
          )}

          <Suspense fallback={null}>
            <MapInfoModal isOpen={isMapInfoOpen} onToggle={setIsMapInfoOpen} />
          </Suspense>

          {activePanel &&
            PANEL_CONFIG[activePanel] &&
            (() => {
              const config = PANEL_CONFIG[activePanel]
              const data = panelData[activePanel]

              if (
                config.shouldRender &&
                !config.shouldRender(data, panelContext)
              ) {
                return null
              }

              const PanelComponent = config.component
              const props = config.getProps(data, panelContext)

              return (
                <Suspense fallback={null} key={activePanel}>
                  <div style={{ zIndex: config.zIndex }}>
                    <PanelComponent {...props} />
                  </div>
                </Suspense>
              )
            })()}

          <Suspense fallback={null}>
            <ZoneCreateSuccessModal
              isOpen={showCreateSuccessModal}
              zone={createdZone}
              onClose={() => {
                setShowCreateSuccessModal(false)
                setCreatedZone(null)
              }}
              onViewZone={() => {
                if (createdZone) {
                  openPanel('details', createdZone)
                }
              }}
            />
          </Suspense>

          <Suspense fallback={null}>
            <ZoneUpdateSuccessModal
              isOpen={showUpdateSuccessModal}
              zone={updatedZone}
              onClose={() => {
                setShowUpdateSuccessModal(false)
                setUpdatedZone(null)
              }}
              onViewZone={() => {
                if (updatedZone) {
                  openPanel('details', updatedZone)
                  setShowUpdateSuccessModal(false)
                  setUpdatedZone(null)
                }
              }}
            />
          </Suspense>
        </div>
      </div>

      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isOpen={notification.isOpen}
        onClose={() => setNotification(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}
